<template>
  <div class="relative py-2.5 px-5">
    <div v-if="isLoading === false">
      <div class="w-full">
        <div v-if="selectedRoundTable && selectedRoundTable.id" class="flex flex-col sm:flex-row sm:space-x-3 items-center">
          <div class="relative h-12 w-full sm:w-8/12 md:w-9/12 xl:w-10/12">
            <button @click="openSelectRoundTable()" class="relative h-12 flex items-center w-full bg-gray-100 border-solid border border-gray-300 rounded-md pl-4 pr-10 py-3 text-left cursor-pointer focus:outline-none">
              <div 
                v-if="selectedRoundTable.type === 'written' || selectedRoundTable.type === 'roundtable'"
                title="Written RoundTable"
                class="w-6 h-6 bg-blue rounded-full flex items-center justify-center"
              >
                <img src="/static/roundtable_icons/written_icon.svg" class="w-3 h-3"/>
              </div>
              <div
                v-else
                class="w-6 h-6 bg-green-light rounded-full flex items-center justify-center"
                title="Verbal RoundTable"
              >
                <img src="/static/roundtable_icons/verbal_icon.svg" class="w-3 h-3"/>
              </div>
              <div class="flex items-center truncate" style="width: calc(100% - 24px)">
                <span class="ml-2.5 block font-semibold text-gray-700">
                  {{ selectedRoundTable.title }}
                </span>
                <span class="ml-2.5 block text-gray-700">
                  {{ roundTableSelectedCreatedAt }}
                </span>
              </div>
              <div class="ml-3 absolute text-gray-700 inset-y-0 right-0 flex items-center pr-5 pointer-events-none">
                <i v-if="!isSelectRoundTableOpen" class="fa-solid fa-angle-down"></i>
                <i v-else class="fa-solid fa-angle-up"></i>
              </div>
            </button>
            <CustomSelect
              @selectedOption="setSelectRoundTable"
              :isOpen="isSelectRoundTableOpen"
              :toLoop="allRoundTables"
              class="w-full"
              :selectedId="selectedRoundTable.id"
            />
          </div>
          <button
            @click="jumpToRoundTable(selectedRoundTable)"
            class="bg-primary mt-2.5 sm:mt-0 py-3 px-4 w-full sm:w-4/12 md:w-3/12 xl:w-2/12 h-12 rounded text-white font-semibold truncate"
          >
            Jump to RoundTable
          </button>
        </div>
        <div v-else class="py-5">
          This student does not have RoundTables to show
        </div>
      </div>
      <IndividualWrittenSummary
        v-if="selectedRoundTable.type === 'written'"
        class="mt-4"
      />
      <IndividualLiveSummary
        v-if="selectedRoundTable.type === 'live_discussion'"
        :selectedParticipationSummary="getStudentSummaryLD"
        class="mt-4"
      />
    </div>
    <div v-else class="flex justify-center mt-6 mb-2">
      <pl-spinner />
    </div>
  </div>
</template>
<script>
  import Vue from 'vue';
  import store from 'store';
  import { EventBus } from 'utils/event-bus';
  import moment from 'moment';
  import { mapGetters } from 'vuex';
  import onlineRoundtableSockets from 'mixins/sockets/onlineRoundtableSocketEvents';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';

  Vue.directive('clickOutside', {
    bind: function (el, binding, vnode) {
      el.clickOutsideEvent = function (event) {
        if (!(el == event.target || el.contains(event.target)))
          vnode.context[binding.expression](event);
      };
      document.body.addEventListener('click', el.clickOutsideEvent);
    },
    unbind: function (el) {
      document.body.removeEventListener('click', el.clickOutsideEvent);
    },
  });
  export default Vue.extend({
    data() {
      return {
        isSelectRoundTableOpen: false,
        selectedRoundTable: {},
        typeSelectedRoundTable: '',
        student_submissions: {},
        isLoading: false,
      };
    },
    mixins: [onlineRoundtableSockets, courseSocketMixin],
    components: {
      CustomSelect: () => import('./SelectRoundTableOption.vue'),
      IndividualLiveSummary: () => import('./IndividualLiveStudentSummary.vue'),
      IndividualWrittenSummary: () =>
        import('./IndividualWrittenStudentSummary.vue'),
    },
    watch: {
      '$route.params.studentID': async function () {
        const { studentID } = this.$route.params;
        if (this.getProfile.id === studentID) {
          this.isMountedFromStudentToGetRoundTables();
        } else {
          this.leaveRoundtableSocket(this.selectedRoundTable.id);
          await this.handleMountingTeacher();
        }
      },
    },
    computed: {
      ...mapGetters([
        'getStudentSummary',
        'getStudentSummaryLD',
        'getCourseDetail',
        'getCurrentRT',
        'getCurrentDiscussion',
        'hasTeacherPermissionsInCourse',
        'getCourseLayoutFullScreenStatus',
        'getProfile',
        'getStudentAssessments',
      ]),
      roundTableSelectedCreatedAt: function () {
        const date = moment(this.selectedRoundTable.created_at).format(
          'MMMM Do YYYY'
        );
        return date;
      },
    },
    methods: {
      showTemplateModal() {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        EventBus.$emit('showTemplateModal', 'written');
      },
      openSelectRoundTable: function () {
        this.isSelectRoundTableOpen = !this.isSelectRoundTableOpen;
      },
      isMountedFromStudentToGetRoundTables: function () {
        this.isLoading = true;
        const { courseID } = this.$route.params;
        store
          .dispatch('setMyCourseStudentActivities', { courseID })
          .then((activities) => {
            this.allRoundTables = activities;
            /* Set Online RoundTable */
            if (activities[0].type === 'roundtable') {
              this.typeSelectedRoundTable = 'roundtable';
              const selectedRT = this.getCourseDetail.roundtables.find(
                (roundtable) => roundtable.id === activities[0].id
              );
              this.selectedRoundTable = selectedRT;
              store.dispatch('myRTIdentity', selectedRT.id);
              const query = {
                submission_id: this.allRoundTables[0].users[0].submission.id,
              };
              const request = {
                roundtableID: this.allRoundTables[0].id,
                query,
              };
              store
                .dispatch('fetchRoundtableSettings', request)
                .then(async () => {
                  await this.$store.dispatch(
                    'getSubmissionSummary',
                    this.getCurrentRT.submissions[0].id
                  );
                  this.isLoading = false;
                });
            }
            /* Set Live Discussion */
            if (activities[0].type === 'live_discussion') {
              this.typeSelectedRoundTable = 'live_discussion';
              const selectedLiveDiscussion =
                this.getCourseDetail.live_discussions.find(
                  (live) => live.id === activities[0].id
                );
              activities.map((activity) => {
                if (selectedLiveDiscussion.id === activity.id) {
                  this.selectedRoundTable = activity;
                  const participationId = activity.users[0].participation.id;
                  store.dispatch('fetchLiveDiscussion', activity.id);
                  store
                    .dispatch('fetchParticipationSummary', participationId)
                    .then(() => (this.isLoading = false));
                }
              });
            }
          });
      },
      /* This code needs review */
      setSelectRoundTable: async function (selectedOption) {
        this.isLoading = true;
        const { courseID, studentID } = this.$route.params;
        if (this.getProfile.role === 'student') {
          if (selectedOption.type === 'roundtable') {
            this.typeSelectedRoundTable = selectedOption.type;
            let onlineRTs = [];
            return store.dispatch('setMyCourseStudentActivities', { courseID, studentID }).then((activities) => {
              activities.map((activity) => onlineRTs.push(activity));
              this.allRoundTables = onlineRTs;
              const selectedRT = this.getCourseDetail.roundtables.find(
                (roundtable) => {
                  return roundtable.id === selectedOption.id;
                }
              );
              this.selectedRoundTable = selectedRT;
              store.dispatch('myRTIdentity', selectedRT.id);
              this.isSelectRoundTableOpen = false;
              const RT = this.allRoundTables.find((rt) => {
                return rt.title === selectedOption.title;
              });
              const query = { submission_id: RT.users[0].submission.id };
              const request = { roundtableID: RT.id, query };
              store
                .dispatch('fetchRoundtableSettings', request)
                .then(async () => {
                  await this.$store.dispatch(
                    'getSubmissionSummary',
                    this.getCurrentRT.submissions[0].id
                  );
                  this.isLoading = false;
                });
            });
          } else if (selectedOption.type === 'live_discussion') {
            this.typeSelectedRoundTable = selectedOption.type;
            return store.dispatch('setMyCourseStudentActivities', { courseID, studentID }).then((activities) => {
              activities.map((activity) => {
                if (selectedOption.id === activity.id) {
                  this.selectedRoundTable = activity;
                  let participationId = activity.users[0].participation.id;
                  store.dispatch('fetchLiveDiscussion', activity.id);
                  store
                    .dispatch('fetchParticipationSummary', participationId)
                    .then(() => {
                      this.isLoading = false;
                      this.isSelectRoundTableOpen = false;
                    });
                }
              });
            });
          }
        } else {
          if (selectedOption.type === 'roundtable') {
            let onlineRTs = [];
            this.typeSelectedRoundTable = selectedOption.type;
            const activities = await store.dispatch('setCourseStudentActivities',{ courseID, studentID });
            activities.map((activity) => onlineRTs.push(activity));
            this.allRoundTables = onlineRTs;
            const roundtable = await store.dispatch('fetchRoundtable', selectedOption.id);
            this.selectedRoundTable = roundtable;
            this.isSelectRoundTableOpen = false;
            EventBus.$emit('selectedRT', roundtable);
            const RT = this.allRoundTables.find((rt) => {
              return rt.title === selectedOption.title;
            });
            const query = { submission_id: RT.users[0].submission.id };
            const request = { roundtableID: RT.id, query };
            const roundtable_settings = await store.dispatch('fetchRoundtableSettings',request);
            await this.$store.dispatch('getSubmissionSummary',roundtable_settings.submissions[0].id);
            this.setupCourseSocket(courseID);
            this.setupRoundtableSocket(this.selectedRoundTable.id);
            const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
            if (members_channel) {
              this.members_course_socket = members_channel.members;
            }
            this.isLoading = false;
          } else if (selectedOption.type === 'live_discussion') {
            this.typeSelectedRoundTable = selectedOption.type;
            return store.dispatch('setCourseStudentActivities', { courseID, studentID }).then((activities) => {
              activities.map((activity) => {
                if (selectedOption.id === activity.id) {
                  this.selectedRoundTable = activity;
                  /* Send RT to Header */
                  EventBus.$emit('selectedRT', activity);
                  let participationId = activity.users[0].participation.id;
                  store.dispatch('fetchLiveDiscussion', activity.id);
                  store
                    .dispatch('fetchParticipationSummary', participationId)
                    .then(() => {
                      this.isLoading = false;
                      this.isSelectRoundTableOpen = false;
                    });
                }
              });
            });
          }
        }
      },
      isMountedFromTeacherToGetRoundTables: async function () {
        this.isLoading = true;

        const { courseID, studentID } = this.$route.params;
        const { roundtableId } = this.$route.query;

        const activities = await store.dispatch('setCourseStudentActivities', {courseID, studentID });

        this.allRoundTables = activities;

        if (activities.length === 0) {
          this.isLoading = false;
          return;
        }

        const first_roundtable = roundtableId ? activities.filter((a) => a.id === roundtableId)[0] : activities[0];

        if (first_roundtable.type === 'roundtable') {
          this.typeSelectedRoundTable = 'roundtable';
          if (first_roundtable.sequence_id === null) {
            this.selectedRoundTable = this.getCourseDetail.roundtables.find((roundtable) => roundtable.id === activities[0].id);
            EventBus.$emit('selectedRT', this.selectedRoundTable);
            const query = {
              submission_id: this.allRoundTables[0].users[0].submission.id,
            };
            const request = { roundtableID: this.allRoundTables[0].id, query };
            await store.dispatch('fetchRoundtableSettings', request);
            this.student_submissions = this.getCurrentRT.submissions[0];
            await this.$store.dispatch('getSubmissionSummary', this.student_submissions.id);
            this.setupCourseSocket(courseID);
            this.setupRoundtableSocket(this.selectedRoundTable.id);
            const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
            if (members_channel) {
              this.members_course_socket = members_channel.members;
            }
            this.isLoading = false;
            return;
          }
          if (first_roundtable.sequence_id !== null) {
            if (!this.getCourseDetail) {
              await store.dispatch('fetchCourseDetails', courseID);
            }
            const sequence_ = this.getCourseDetail.sequences.filter((sequence) => sequence.id === first_roundtable.sequence_id);
            sequence_.find((sequence) => {
              if (sequence.roundtables === null) return;
              return sequence.roundtables.map(async (roundtable) => {
                if (roundtableId && roundtableId === roundtable.id) {
                  const query = {
                    submission_id: this.allRoundTables.filter((a) => a.id === roundtableId)[0].users[0].submission.id,
                  };
                  const request = {
                    roundtableID: roundtableId,
                    query,
                  };
                  const rt = await store.dispatch('fetchRoundtableSettings',request);
                  this.selectedRoundTable = rt;
                  this.student_submissions = this.getCurrentRT.submissions[0];
                  await this.$store.dispatch('getSubmissionSummary', this.student_submissions.id);
                  this.setupCourseSocket(courseID);
                  this.setupRoundtableSocket(this.selectedRoundTable.id);
                  const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
                  if (members_channel) {
                    this.members_course_socket = members_channel.members;
                  }
                  this.isLoading = false;
                }
                if(roundtable.id === first_roundtable.id && !roundtableId) {
                  const query = {
                    submission_id: this.allRoundTables[0].users[0].submission.id,
                  };
                  const request = {
                    roundtableID: this.allRoundTables[0].id,
                    query,
                  };
                  const rt = await store.dispatch('fetchRoundtableSettings',request);
                  this.selectedRoundTable = rt;
                  this.student_submissions = this.getCurrentRT.submissions[0];
                  await this.$store.dispatch('getSubmissionSummary', this.student_submissions.id);
                  this.setupCourseSocket(courseID);
                  this.setupRoundtableSocket(this.selectedRoundTable.id);
                  const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
                  if (members_channel) {
                    this.members_course_socket = members_channel.members;
                  }
                  this.isLoading = false;
                }
                
              });
            });
          }
        }
        if (first_roundtable.type === 'live_discussion') {
          this.typeSelectedRoundTable = 'live_discussion';
          if (first_roundtable.sequence_id === null) {
            this.selectedRoundTable = activities[0];
            EventBus.$emit('selectedRT', this.selectedRoundTable);
            const participationId =
              activities[0].users[0].participation.id;
            await store.dispatch(
              'fetchLiveDiscussion',
              this.selectedRoundTable.id
            );
            await store.dispatch('fetchParticipationSummary', participationId);
            this.isLoading = false;
            return;
          }
          if (first_roundtable.sequence_id !== null) {
            if (!this.getCourseDetail) {
              await store.dispatch('fetchCourseDetails', courseID);
            }
            const sequence_ = this.getCourseDetail.sequences.filter(
              (sequence) => sequence.id === first_roundtable.sequence_id
            );
            return sequence_.find((sequence) => {
              if (sequence.roundtables === null) return;
              return sequence.roundtables.map(async (roundtable) => {
                activities.map((activity) => {
                  if (roundtable.id === activity.id) {
                    this.selectedRoundTable = activity;
                    EventBus.$emit('selectedRT', activity);
                    const participationId = activity.users[0].participation.id;
                    store.dispatch('fetchLiveDiscussion', activity.id);
                    store.dispatch('fetchParticipationSummary',participationId);
                    this.isLoading = false;
                  }
                });
              });
            });
          }
        }
        if (
          activities[0].type !== 'live_discussion' &&
          activities[0].type !== 'roundtable'
        ) {
          this.selectedRoundTable = {};
          this.isLoading = false;
          return;
        }
      },
      jumpToRoundTable: function () {
        const selectedRoundTable = this.allRoundTables.filter((roundtable) => roundtable.id === this.selectedRoundTable.id);
        const typeOfSelected = selectedRoundTable[0].type;
        if (typeOfSelected === 'live_discussion') {
          this.$router.push({
            name: 'live-discussion.prompt',
            params: {
              courseID: this.$route.params.courseID,
              liveDiscussionID: this.selectedRoundTable.id,
            },
          });
        } else {
          this.$router.push({
            name: 'roundtable.discussion',
            params: {
              courseID: this.$route.params.courseID,
              roundtableID: this.selectedRoundTable.id,
            },
          });
        }
      },
      handleStartDiscussionsTab: async function() {
        const { studentID, courseID } = this.$route.params;
        if (this.getProfile === studentID && this.getProfile.role === 'student') {
          console.log(courseID);
          return;
        }

      },
      handleMountingTeacher: async function() {
        this.isLoading = true;
        const { studentID, courseID } = this.$route.params;
        const { roundtableId } = this.$route.query;

        const studentRoundtables = await store.dispatch('setCourseStudentActivities', { courseID, studentID });
        this.allRoundTables = studentRoundtables;

        if (studentRoundtables.length === 0) {
          this.isLoading = false;
          
          return;
        }

        const studentFirstRoundtable = roundtableId ? studentRoundtables.filter((a) => a.id === roundtableId)[0] : studentRoundtables[0];
        if (studentFirstRoundtable.type === 'live_discussion') {
          this.typeSelectedRoundTable = 'live_discussion';

          if (studentFirstRoundtable.sequence_id === null) {
            this.selectedRoundTable = studentFirstRoundtable;
            EventBus.$emit('selectedRT', studentFirstRoundtable);
            const participationId = studentFirstRoundtable.users[0].participation.id;
            await Promise.all([
              store.dispatch('fetchLiveDiscussion', studentFirstRoundtable.id),
              store.dispatch('fetchParticipationSummary', participationId)
            ]).then(() => {
              this.isLoading = false;
            });
            return;
          } else {
            if (!this.getCourseDetail) {
              await store.dispatch('fetchCourseDetails', courseID);
            }
            const sequence_ = this.getCourseDetail.sequences.filter((sequence) => sequence.id === studentFirstRoundtable.sequence_id);
            return sequence_.find((sequence) => {
              if (sequence.roundtables === null) {
                return;
              }
              return sequence.roundtables.map(async (roundtable) => {
                studentRoundtables.map((activity) => {
                  if (roundtable.id === activity.id) {
                    this.selectedRoundTable = activity;
                    EventBus.$emit('selectedRT', activity);
                    const participationId = activity.users[0].participation.id;
                    store.dispatch('fetchLiveDiscussion', activity.id);
                    store.dispatch('fetchParticipationSummary',participationId);
                    this.isLoading = false;
                  }
                });
              });
            });
          }
        }

        if (studentFirstRoundtable.type === 'roundtable') {
          this.typeSelectedRoundTable = 'roundtable';
          if (studentFirstRoundtable.sequence_id === null) {
            this.selectedRoundTable = this.getCourseDetail.roundtables.find((roundtable) => roundtable.id === studentFirstRoundtable.id);
            EventBus.$emit('selectedRT', studentFirstRoundtable);
            const query = {
              submission_id: studentFirstRoundtable.users[0].submission.id,
            };
            const request = { roundtableID: studentFirstRoundtable.id, query };
            await store.dispatch('fetchRoundtableSettings', request);
            this.student_submissions = this.getCurrentRT.submissions[0];
            await this.$store.dispatch('getSubmissionSummary', this.student_submissions.id);
            this.setupCourseSocket(courseID);
            this.setupRoundtableSocket(this.selectedRoundTable.id);
            const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
            if (members_channel) {
              this.members_course_socket = members_channel.members;
            }
            this.isLoading = false;
            return;
          }
          if (studentFirstRoundtable.sequence_id !== null) {
            if (!this.getCourseDetail) {
              await store.dispatch('fetchCourseDetails', courseID);
            }
            const sequence_ = this.getCourseDetail.sequences.filter((sequence) => sequence.id === studentFirstRoundtable.sequence_id);
            sequence_.find((sequence) => {
              if (sequence.roundtables === null) return;
              return sequence.roundtables.map(async (roundtable) => {
                if (roundtableId && roundtableId === roundtable.id) {
                  const query = {
                    submission_id: this.allRoundTables.filter((a) => a.id === roundtableId)[0].users[0].submission.id,
                  };
                  const request = {
                    roundtableID: roundtableId,
                    query,
                  };
                  const rt = await store.dispatch('fetchRoundtableSettings',request);
                  this.selectedRoundTable = rt;
                  this.student_submissions = this.getCurrentRT.submissions[0];
                  await this.$store.dispatch('getSubmissionSummary', this.student_submissions.id);
                  this.setupCourseSocket(courseID);
                  this.setupRoundtableSocket(this.selectedRoundTable.id);
                  const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
                  if (members_channel) {
                    this.members_course_socket = members_channel.members;
                  }
                  this.isLoading = false;
                }
                if(roundtable.id === studentFirstRoundtable.id && !roundtableId) {
                  const query = {
                    submission_id: this.allRoundTables[0].users[0].submission.id,
                  };
                  const request = {
                    roundtableID: this.allRoundTables[0].id,
                    query,
                  };
                  const rt = await store.dispatch('fetchRoundtableSettings',request);
                  this.selectedRoundTable = rt;
                  this.student_submissions = this.getCurrentRT.submissions[0];
                  await this.$store.dispatch('getSubmissionSummary', this.student_submissions.id);
                  this.setupCourseSocket(courseID);
                  this.setupRoundtableSocket(this.selectedRoundTable.id);
                  const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
                  if (members_channel) {
                    this.members_course_socket = members_channel.members;
                  }
                  this.isLoading = false;
                }
              });
            });
          }
        }
        if (studentFirstRoundtable.type !== 'live_discussion' && studentFirstRoundtable.type !== 'roundtable') {
          this.selectedRoundTable = {};
          this.isLoading = false;
          return;
        }
      }
    },
    mounted: async function () {
      const { studentID, courseID } = this.$route.params;
      const { handleMountingTeacher } = this;
      if (this.getProfile === studentID && this.getProfile.role === 'student') {
        return this.isMountedFromStudentToGetRoundTables();
      }
      await handleMountingTeacher();
      this.setupCourseSocket(courseID);
      this.setupRoundtableSocket(this.selectedRoundTable.id);
      const members_channel = await window.pusher.channels.channels[`presence-course-${this.$route.params.courseID}`];
      if (members_channel) {
        this.members_course_socket = members_channel.members;
      }
    },
  });
</script>
<style scoped>
  .loader,
  .loader:after {
    border-radius: 50%;
    width: 16px;
    height: 16px;
    margin-top: 1px;
  }
  .loader {
    border-top: 3px solid rgba(255, 255, 255, 0.2);
    border-right: 3px solid rgba(255, 255, 255, 0.2);
    border-bottom: 3px solid rgba(255, 255, 255, 0.2);
    border-left: 3px solid #ffffff;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    -webkit-animation: load8 1.1s infinite linear;
    animation: load8 1.1s infinite linear;
  }
</style>



// WEBPACK FOOTER //
// DiscussionStudentTab.vue?6d167cdf